
import { defineComponent, onMounted, reactive } from 'vue'

// @ui-kit
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { WarehouseT } from '@/core/types/warehouses.types'
import { CompanyT } from '@/core/types/companies.types'
import { NavigationTMS, TransportTypeTMS } from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @realisations
import { warehouses } from '@/core/realisations/warehouses.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// @constants
import { INTRA_OPTIONS } from '@/core/constants/tms.constants'

export default defineComponent({
    components: {
        NewSelect,
        NewButton,
        NewInput,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        //

        const state = reactive({
            navigationIds: [] as number[],
            warehouseIds: [] as number[],
            transportTypeIds: [] as number[],
            companyId: null as number | null,
            invoiceNumber: null as string | null,
            orderNumber: null as string | null,
            isIntraCity: null as number | null,
        })

        const data_state = reactive({
            navigations: {
                is_loading: false,
                is_failed: false,
                list: [] as NavigationTMS[],
            },
            warehouses: {
                is_loading: false,
                is_failed: false,
                list: [] as WarehouseT[],
            },
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CompanyT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            transport_types: {
                is_loading: false,
                is_failed: false,
                list: [] as TransportTypeTMS[],
            },
        })

        //

        const loadNavigations = async () => {
            try {
                data_state.navigations.is_loading = true

                const response = await tmsAPI.getNavigations()
                data_state.navigations.list = response.data.data

                data_state.navigations.is_failed = false
            } catch (error) {
                data_state.navigations.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка направлений произошла ошибка',
                })
            } finally {
                data_state.navigations.is_loading = false
            }
        }

        const loadWarehouses = async () => {
            try {
                data_state.warehouses.is_loading = true

                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                data_state.warehouses.is_failed = false
            } catch (error) {
                data_state.warehouses.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка филиалов произошла ошибка',
                })
            } finally {
                data_state.warehouses.is_loading = false
            }
        }

        const loadTransportTypes = async () => {
            try {
                data_state.transport_types.is_loading = true

                const response = await tmsAPI.getTransportTypes()
                data_state.transport_types.list = response.data.data

                data_state.transport_types.is_failed = false
            } catch (error) {
                data_state.transport_types.is_failed = true
                console.error(error)
            } finally {
                data_state.transport_types.is_loading = false
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const onFiltersChanged = () => {
            if (props.disabled || props.loading) return
            emit('submit', { ...state })
        }

        const onFiltersReset = () => {
            state.navigationIds = []
            state.warehouseIds = []
            state.transportTypeIds = []
            state.companyId = null
            state.invoiceNumber = null
            state.orderNumber = null
            state.isIntraCity = null

            emit('reset')
        }

        //

        onMounted(() => {
            loadCompanies(1)
            loadWarehouses()
            loadNavigations()
            loadTransportTypes()
        })

        //

        return {
            state,
            data_state,
            //
            onFiltersChanged,
            onFiltersReset,
            //
            loadNavigations,
            loadWarehouses,
            loadTransportTypes,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            INTRA_OPTIONS,
        }
    },
})
